import { useState, useEffect, useCallback, useRef } from "react";
import { createContext, useContext } from "react";

const CartContext = createContext(null);

export const useCart = () => {
  const [cart, setCart] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasLoaded = useRef(false);

  // Helper function to generate consistent cart item key
  const generateCartItemKey = useCallback(
    (productId, size = null, color = null) => {
      const sizeKey = size?.sku || size?.id || size?.name || "nosize";
      const colorKey = color?.sku || color?.id || color?.name || "nocolor";
      return `${productId}_${sizeKey}_${colorKey}`;
    },
    [],
  );

  // Load cart from localStorage on mount
  useEffect(() => {
    if (hasLoaded.current) return;
    hasLoaded.current = true;

    const loadCart = () => {
      try {
        const savedCart = localStorage.getItem("cart");
        if (savedCart) {
          const parsedCart = JSON.parse(savedCart);
          if (Array.isArray(parsedCart)) {
            const validCart = parsedCart
              .filter(
                (item) =>
                  item &&
                  item.id &&
                  item.name &&
                  typeof item.quantity === "number" &&
                  item.quantity > 0,
              )
              .map((item) => ({
                ...item,
                price: typeof item.price === "number" ? item.price : 0,
                quantity: typeof item.quantity === "number" ? item.quantity : 1,
                selectedSize: item.selectedSize || null,
                selectedColor: item.selectedColor || null,
                // Ensure cartItemKey exists and is consistent
                cartItemKey:
                  item.cartItemKey ||
                  generateCartItemKey(
                    item.id,
                    item.selectedSize,
                    item.selectedColor,
                  ),
                addedAt: item.addedAt || Date.now(),
                updatedAt: Date.now(),
              }));
            setCart(validCart);
          } else {
            setCart([]);
          }
        } else {
          setCart([]);
        }
      } catch (err) {
        console.error("Failed to parse cart:", err);
        localStorage.removeItem("cart");
        setError("Failed to load cart");
        setCart([]);
      }
    };

    loadCart();
  }, [generateCartItemKey]);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    if (cart.length === 0 && !hasLoaded.current) return;
    try {
      localStorage.setItem("cart", JSON.stringify(cart));
      if (cart.length > 0) {
        const cartId = localStorage.getItem("cartId") || `cart_${Date.now()}`;
        localStorage.setItem("cartId", cartId);
      } else {
        localStorage.removeItem("cartId");
      }
    } catch (err) {
      setError("Failed to save cart");
    }
  }, [cart]);

  // Backup before page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.setItem("cart_backup", JSON.stringify(cart));
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [cart]);

  // ============ CART CALCULATIONS ============
  const cartCount = cart.reduce(
    (count, item) => count + (item.quantity || 1),
    0,
  );

  const cartTotal = cart.reduce((total, item) => {
    const basePrice = item.price || 0;
    const sizeAdjustment = item.selectedSize?.price_adjustment || 0;
    const colorAdjustment = item.selectedColor?.price_adjustment || 0;
    const unitPrice = basePrice + sizeAdjustment + colorAdjustment;
    return total + unitPrice * (item.quantity || 1);
  }, 0);

  const isEmpty = cart.length === 0;

  // ============ CART ACTIONS ============

  /**
   * Add product to cart with size/color selection
   * @param {Object} product - Product object
   * @param {Object} options - Selection options
   * @param {Object} options.size - Selected size { name, sku, stock_quantity, price_adjustment }
   * @param {Object} options.color - Selected color { name, hex, sku, stock_quantity, price_adjustment, image }
   */
  const addToCart = useCallback(
    async (product, options = {}) => {
      setIsLoading(true);
      setError(null);

      try {
        if (!product || !product.id)
          throw new Error("Invalid product: missing ID");

        const { size = null, color = null } = options;
        const quantity = Number(product.quantity) || 1;
        if (isNaN(quantity) || quantity < 1)
          throw new Error("Invalid quantity");

        // ===== VALIDATE SIZE SELECTION =====
        const hasSizes =
          product.attributes?.has_sizes ||
          product.has_sizes ||
          product.sizes?.length > 0;
        const hasColors =
          product.attributes?.has_colors ||
          product.has_colors ||
          product.colors?.length > 0;

        // Only validate if the product actually has sizes/colors
        if (hasSizes && !size) {
          // Don't throw error for related products that might not have size selection
          // Just use default size if available
          if (product.sizes && product.sizes.length > 0) {
            // Use the first available size as default
            const defaultSize =
              product.sizes.find((s) => s.stock_quantity > 0) ||
              product.sizes[0];
            if (defaultSize) {
              options.size = defaultSize;
            }
          } else if (product.size_options && product.size_options.length > 0) {
            const defaultSize =
              product.size_options.find((s) => s.stock_quantity > 0) ||
              product.size_options[0];
            if (defaultSize) {
              options.size = defaultSize;
            }
          }
        }

        if (hasColors && !color) {
          if (product.colors && product.colors.length > 0) {
            const defaultColor =
              product.colors.find((c) => c.stock_quantity > 0) ||
              product.colors[0];
            if (defaultColor) {
              options.color = defaultColor;
            }
          } else if (
            product.color_options &&
            product.color_options.length > 0
          ) {
            const defaultColor =
              product.color_options.find((c) => c.stock_quantity > 0) ||
              product.color_options[0];
            if (defaultColor) {
              options.color = defaultColor;
            }
          }
        }

        // Re-get size and color after potential defaults
        const finalSize = options.size || size;
        const finalColor = options.color || color;

        // ===== VALIDATE SIZE STOCK =====
        if (
          finalSize &&
          finalSize.stock_quantity !== undefined &&
          finalSize.stock_quantity < quantity
        ) {
          throw new Error(
            `المقاس ${finalSize.name} - الكمية المتاحة: ${finalSize.stock_quantity} فقط`,
          );
        }

        // ===== VALIDATE COLOR STOCK =====
        if (
          finalColor &&
          finalColor.stock_quantity !== undefined &&
          finalColor.stock_quantity < quantity
        ) {
          throw new Error(
            `اللون ${finalColor.name} - الكمية المتاحة: ${finalColor.stock_quantity} فقط`,
          );
        }

        // ===== MIN/MAX QUANTITY =====
        const minQty = product.min_order_quantity || 1;
        const maxQty = product.max_order_quantity || null;
        const availableStock =
          product.available_quantity || product.stock_quantity || 0;

        if (quantity < minQty) {
          throw new Error(`الحد الأدنى للطلب هو ${minQty} قطعة`);
        }
        if (maxQty && quantity > maxQty) {
          throw new Error(`الحد الأقصى للطلب هو ${maxQty} قطعة`);
        }
        if (availableStock > 0 && quantity > availableStock) {
          throw new Error(`الكمية المتاحة: ${availableStock} فقط`);
        }

        // ===== CALCULATE PRICE WITH ADJUSTMENTS =====
        const basePrice =
          Number(product.price) || Number(product.base_price) || 0;
        const sizeAdjustment = finalSize?.price_adjustment || 0;
        const colorAdjustment = finalColor?.price_adjustment || 0;
        const finalPrice = basePrice + sizeAdjustment + colorAdjustment;

        // ===== GENERATE UNIQUE CART ITEM KEY =====
        const cartItemKey = generateCartItemKey(
          product.id,
          finalSize,
          finalColor,
        );

        // Get image URL properly
        const getImageUrl = () => {
          // Check for color image first
          if (finalColor?.image) {
            if (typeof finalColor.image === "string") return finalColor.image;
            if (finalColor.image.url) return finalColor.image.url;
            if (finalColor.image.data_url) return finalColor.image.data_url;
          }
          // Check product image
          if (product.image) {
            if (typeof product.image === "string") return product.image;
            if (product.image.url) return product.image.url;
            if (product.image.data_url) return product.image.data_url;
          }
          if (product.main_image) {
            if (typeof product.main_image === "string")
              return product.main_image;
            if (product.main_image.url) return product.main_image.url;
            if (product.main_image.data_url) return product.main_image.data_url;
          }
          if (product.images && product.images.length > 0) {
            const img = product.images[0];
            if (typeof img === "string") return img;
            if (img.url) return img.url;
            if (img.data_url) return img.data_url;
          }
          return "";
        };

        setCart((prevCart) => {
          const existingItemIndex = prevCart.findIndex(
            (item) => item.cartItemKey === cartItemKey,
          );

          if (existingItemIndex > -1) {
            // Update existing item
            const updatedCart = [...prevCart];
            const existingItem = updatedCart[existingItemIndex];
            const newQuantity = existingItem.quantity + quantity;

            // Check stock for combined quantity
            const itemMax =
              existingItem.maxOrderQuantity ??
              maxQty ??
              existingItem.availableStock;
            if (itemMax && newQuantity > itemMax) {
              throw new Error(`الحد الأقصى للطلب هو ${itemMax} قطعة`);
            }

            updatedCart[existingItemIndex] = {
              ...existingItem,
              quantity: newQuantity,
              updatedAt: Date.now(),
            };
            return updatedCart;
          } else {
            // Add new item
            const newItem = {
              // Unique identifiers
              id: product.id,
              cartItemKey,
              name: product.name || "Unknown Product",
              sku: finalSize?.sku || finalColor?.sku || product.sku || "",

              // Pricing
              price: finalPrice,
              basePrice: basePrice,
              sizePriceAdjustment: sizeAdjustment,
              colorPriceAdjustment: colorAdjustment,

              // Quantity & Limits
              quantity: quantity,
              minOrderQuantity: minQty,
              maxOrderQuantity: maxQty,
              availableStock: availableStock,

              // Selected Attributes - CRITICAL for cart item differentiation
              selectedSize: finalSize,
              selectedColor: finalColor,

              // Product Attributes (for reference)
              sizes: product.sizes || [],
              colors: product.colors || [],
              has_sizes: hasSizes,
              has_colors: hasColors,

              // Product Info
              image: getImageUrl(),
              discount: product.discount || product.discount_percentage || 0,
              category: product.category || product.category_name || "",
              description:
                product.short_description || product.description || "",
              inventory_status: product.inventory_status || "in_stock",
              is_sale: product.is_sale || false,

              // Timestamps
              addedAt: Date.now(),
              updatedAt: Date.now(),

              // Metadata for order creation
              metadata: {
                variant_sku: finalSize?.sku || finalColor?.sku || null,
                size_name: finalSize?.name || null,
                color_name: finalColor?.name || null,
                color_hex: finalColor?.hex || null,
              },
            };
            return [...prevCart, newItem];
          }
        });

        return true;
      } catch (err) {
        setError(err.message || "Failed to add item to cart");
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [generateCartItemKey],
  );

  const removeFromCart = useCallback(async (cartItemKey) => {
    setIsLoading(true);
    setError(null);
    try {
      setCart((prevCart) =>
        prevCart.filter((item) => item.cartItemKey !== cartItemKey),
      );
      return true;
    } catch (err) {
      setError(err.message || "Failed to remove item from cart");
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateQuantity = useCallback(
    async (cartItemKey, quantity) => {
      setIsLoading(true);
      setError(null);
      try {
        if (quantity < 1) {
          return await removeFromCart(cartItemKey);
        }

        let updateError = null;
        setCart((prevCart) =>
          prevCart.map((item) => {
            if (item.cartItemKey === cartItemKey) {
              const min = item.minOrderQuantity ?? 1;
              const max = item.maxOrderQuantity ?? null;
              const stock = item.availableStock ?? Infinity;

              if (quantity < min) {
                updateError = `الحد الأدنى للطلب هو ${min} قطعة`;
                return item;
              }
              if (max && quantity > max) {
                updateError = `الحد الأقصى للطلب هو ${max} قطعة`;
                return item;
              }
              if (quantity > stock) {
                updateError = `الكمية المتاحة: ${stock} فقط`;
                return item;
              }
              return { ...item, quantity, updatedAt: Date.now() };
            }
            return item;
          }),
        );

        if (updateError) throw new Error(updateError);
        return true;
      } catch (err) {
        setError(err.message || "Failed to update quantity");
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [removeFromCart],
  );

  const clearCart = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      setCart([]);
      return true;
    } catch (err) {
      setError(err.message || "Failed to clear cart");
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Check if product with specific size/color is in cart
  const isInCart = useCallback(
    (productId, selectedSize = null, selectedColor = null) => {
      const targetKey = generateCartItemKey(
        productId,
        selectedSize,
        selectedColor,
      );
      return cart.some((item) => item.cartItemKey === targetKey);
    },
    [cart, generateCartItemKey],
  );

  // Check if product with id is in cart
  const isProductIdInCart = useCallback(
    (productId) => {
      if (!productId) return false;
      return cart.some((item) => item.id === productId);
    },
    [cart],
  );

  // Get cart item by product ID, size, and color
  const getCartItem = useCallback(
    (productId, selectedSize = null, selectedColor = null) => {
      const targetKey = generateCartItemKey(
        productId,
        selectedSize,
        selectedColor,
      );
      return cart.find((item) => item.cartItemKey === targetKey);
    },
    [cart, generateCartItemKey],
  );

  /**
   * Convert cart items to OrderItemCreateInput format
   */
  const getOrderItems = useCallback(() => {
    return cart.map((item) => ({
      product_id: item.id,
      variant_id: null,
      product_name: item.name,
      product_sku: item.sku || item.id,
      variant_sku: item.metadata?.variant_sku || null,
      product_image: item.image || null,
      quantity: item.quantity,
      unit_price: item.price,
      tax_amount: 0,
      discount_amount:
        (item.discount || 0) > 0
          ? item.price * (item.discount / 100) * item.quantity
          : 0,
      weight: null,
      dimensions: null,
      metadata: {
        ...item.metadata,
        size_price_adjustment: item.sizePriceAdjustment || 0,
        color_price_adjustment: item.colorPriceAdjustment || 0,
        base_price: item.basePrice,
        selected_size: item.selectedSize,
        selected_color: item.selectedColor,
      },
    }));
  }, [cart]);

  const getCartSummary = useCallback(() => {
    return {
      totalItems: cartCount,
      totalAmount: cartTotal,
      itemCount: cart.length,
      items: cart,
      orderItems: getOrderItems(),
    };
  }, [cart, cartCount, cartTotal, getOrderItems]);

  return {
    // State
    cart,
    isLoading,
    error,

    // Cart Info
    cartCount,
    cartTotal,
    isEmpty,
    summary: getCartSummary(),

    // Methods
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartItem,
    isInCart,
    isProductIdInCart,
    getOrderItems,

    // Helper
    setError,
  };
};

// Cart Context Provider
export const CartProvider = ({ children }) => {
  const cart = useCart();
  return <CartContext.Provider value={cart}>{children}</CartContext.Provider>;
};

export const useCartContext = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCartContext must be used within a CartProvider");
  }
  return context;
};
